import { z } from "zod";
import { isPersonalEmail } from "../personalEmail";
import {
  authProvidersOf,
  defaultAuthProvider,
  signInProviderKind,
  type AuthClientSetting,
  type AuthProvider,
} from "./authClientConfig";

/**
 * #1639 C0 (owner, 2026-10-20, reversing the round 9 rule "the grant dialog offers no provider"):
 * the grant dialog's "Sign in with" choice. It decides the provider a new grant pins the person
 * to, and the invitation names that one method. `microsoft` is the workforce provider
 * (`idp: "entra-workforce"`), `email` the External ID email form (`entra-external` with no
 * `domainHint`), as the sign-in page tells them apart (`signInProviderKind`).
 *
 * One rule for the server (`AccessService.grant`, which maps the method to the provider's issuer)
 * and the dialog (which offers only the methods this deployment has and picks the default), so
 * the two never disagree about what "Email" means.
 */
export const SIGN_IN_METHODS = ["microsoft", "email"] as const;
export type SignInMethod = (typeof SIGN_IN_METHODS)[number];
export const signInMethodSchema = z.enum(SIGN_IN_METHODS);

/** The dialog's words for each method. */
export const SIGN_IN_METHOD_LABELS = {
  microsoft: "Microsoft work account",
  email: "Email",
} as const satisfies Record<SignInMethod, string>;

/**
 * The provider a method pins to: the default provider when it is of that kind, else the first of
 * that kind. Undefined when the deployment offers no provider of that kind.
 */
export function signInMethodProvider(
  setting: AuthClientSetting | undefined,
  method: SignInMethod,
): AuthProvider | undefined {
  if (!setting) return undefined;
  const providers = authProvidersOf(setting);
  const ofKind = providers.filter((provider) => signInProviderKind(provider) === method);
  return ofKind.find((provider) => provider.default) ?? ofKind[0];
}

/** The methods this deployment offers, in the dialog's order. */
export function offeredSignInMethods(setting: AuthClientSetting | undefined): SignInMethod[] {
  return SIGN_IN_METHODS.filter((method) => signInMethodProvider(setting, method) !== undefined);
}

function methodOf(provider: AuthProvider | undefined): SignInMethod | undefined {
  if (!provider) return undefined;
  const kind = signInProviderKind(provider);
  return kind === "google" ? undefined : kind;
}

/**
 * The dialog's default for an address, which the admin may change:
 * 1. a provider whose `pinEmailDomains` lists the address's domain: that provider's method;
 * 2. a common personal mail domain (`shared/personalEmail.ts`): Email, when it is offered;
 * 3. the default provider's method;
 * 4. the first method offered.
 * Undefined when the deployment offers no method.
 */
export function defaultSignInMethod(
  setting: AuthClientSetting | undefined,
  email: string,
): SignInMethod | undefined {
  const offered = offeredSignInMethods(setting);
  if (!setting || offered.length === 0) return undefined;
  const address = email.trim().toLowerCase();
  const domain = address.slice(address.lastIndexOf("@") + 1);
  const providers = authProvidersOf(setting);
  const pinned = methodOf(
    domain ? providers.find((provider) => provider.pinEmailDomains?.includes(domain)) : undefined,
  );
  if (pinned && offered.includes(pinned)) return pinned;
  if (isPersonalEmail(address) && offered.includes("email")) return "email";
  const fallback = methodOf(defaultAuthProvider(providers));
  if (fallback && offered.includes(fallback)) return fallback;
  return offered[0];
}

/**
 * #2023 (owner 2026-10-26): how the grant dialog shows "Sign in with", over the default above.
 * - `none`: the dialog says nothing. Fewer than two methods are offered (`method` undefined, the
 *   server's pin decides), or the role allows one method only (`allowed`: Platform admin under the
 *   workforce rule, an admin role under the MFA floor), which is then `method`. With none allowed,
 *   `method` is undefined and the dialog says why.
 * - `line`: one line naming `method`, with Change to pick another. `chosen` is the admin's pick,
 *   kept while it is allowed; otherwise the address default (`defaultSignInMethod`).
 * The grant always sends `method` when there is one, so the server still pins and audits it.
 */
export interface SignInMethodChoice {
  shown: "none" | "line";
  method: SignInMethod | undefined;
}

export function signInMethodChoice(
  setting: AuthClientSetting | undefined,
  email: string,
  options: { allowed?: readonly SignInMethod[]; chosen?: SignInMethod } = {},
): SignInMethodChoice {
  const offered = offeredSignInMethods(setting);
  if (offered.length < 2) return { shown: "none", method: undefined };
  const { allowed, chosen } = options;
  const usable = allowed ? offered.filter((method) => allowed.includes(method)) : offered;
  if (usable.length < 2) return { shown: "none", method: usable[0] };
  if (chosen && usable.includes(chosen)) return { shown: "line", method: chosen };
  const fallback = defaultSignInMethod(setting, email);
  return { shown: "line", method: fallback && usable.includes(fallback) ? fallback : usable[0] };
}
